import { IUserUpdateInputModel, NotificationPreferencesInputModel } from 'inputModels';
import { useNavigate } from 'react-router-dom';
import { serialize } from 'object-to-formdata';
import { useEffect, useRef } from 'react';
import {
  IRefRegisterAddressData,
  IRefRegisterProfileData,
  IRefRegisterServiceData,
  IRefRegisterUrlData,
  IRefRegisterTypeServiceData,
} from 'entities';
import { IAddressViewModel } from 'viewModels';
import { BasicButton, NavHeader } from '../../components';
import { RegisterAddress } from '../../components/RegisterAddress';
import { RegisterHeader } from '../../components/RegisterHeader';
import { RegisterProfile } from '../../components/RegisterProfile';
import { RegisterService } from '../../components/RegisterService';
import { RegisterUrl } from '../../components/RegisterUrl';
import { useApp, useRegisterForm } from '../../context';
import { UserController } from '../../controller/UserController';
import { TypeContact } from '../../types/enums';
import { Content } from './styles';
import { useAlert } from '../../hooks/useAlert';
import { CompanyController } from '../../controller/CompanyController';
import { useSpecialties } from '../../context/specialties';
import { useCorrespondences } from '../../context/correspondences';
import { RegisterTypeServices } from '../../components/RegisterTypeServices';
import { InviteFriend } from '../../components/InviteFriend';
import { prepareExternalContactsForSubmit } from '../../utils/validateExternalContact';

export function LegalPerson() {
  const navigate = useNavigate();
  const alert = useAlert();
  const app = useApp();
  const {
    setFormSubmited,
    setActiveForm,
    isLoading,
    firstName,
    lastName,
    email,
    setTradingName,
    setAddress,
    setIsServiceProvider,
    setPresentationLetter,
    setPictureProfile,
    setExternalContacts,
    setLinkFacebook,
    setLinkInstagram,
    setLinkLinkedIn,
    setLinkSite,
    setLinkTwitter,
    setUrl,
  } = useRegisterForm();
  const { setSpecialtiesSelectedByUser } = useSpecialties();
  const { setCorrespondencesSelectedByUser } = useCorrespondences();
  const addressDataRef = useRef<IRefRegisterAddressData>(null);
  const serviceDataRef = useRef<IRefRegisterServiceData>(null);
  const serviceTypeDataRef = useRef<IRefRegisterTypeServiceData>(null);
  const profileDataRef = useRef<IRefRegisterProfileData>(null);
  const urlDataRef = useRef<IRefRegisterUrlData>(null);

  const handleUpdateProfile = async () => {
    setFormSubmited(true);
    const addressData = addressDataRef.current?.handleUpdateAddressData();
    const serviceData = serviceDataRef.current?.handleUpdateServiceData();
    const profileData = await profileDataRef.current?.handleUpdateProfileData();
    const urlData = await urlDataRef.current?.handleUpdateUrlData();
    const serviceTypeData = serviceTypeDataRef.current?.handleUpdateTypeServiceData();

    if (!addressData || !serviceData || !profileData || !urlData || !serviceTypeData) {
      alert.show({
        message: 'Falha ao tentar buscar os dados do formulário, tente novamente!',
        type: 'error',
      });
      return;
    }
    const hasError =
      addressData.hasError || serviceData.hasError || urlData.hasError || serviceTypeData.hasError;
    if (hasError) {
      if (addressData.hasError) {
        addressDataRef.current?.handleFocusOnError();
        setActiveForm('registerAddress');
        return;
      }
      if (serviceData.hasError) {
        serviceDataRef.current?.handleFocusOnError();
        setActiveForm('registerService');
        return;
      }
      if (serviceTypeData.hasError) {
        serviceTypeDataRef.current?.handleFocusOnError();
        setActiveForm('registerTypeService');
        return;
      }
      return;
    }

    const update: IUserUpdateInputModel = {
      firstName,
      lastName,
      email,
      tradingName: addressData.tradingName,
      address: addressData.address,
      isServiceProvider: serviceData.isProviderService,
      //districtsId: serviceData.districtsSelected.map((district) => district.id),
      specialties: serviceTypeData.specialtiesSelected.map((specialty) => specialty.id),
      correspondences: serviceTypeData.correspondencesSelected.map(
        (correspondence) => correspondence.id,
      ),
      profilePictureId: profileData.pictureProfile?.id,
      externalContacts: prepareExternalContactsForSubmit(profileData.externalContacts),
      presentation: profileData.presentationLetter,
      linkFacebook: profileData.linkFacebook,
      linkInstagram: profileData.linkInstagram,
      linkLinkedIn: profileData.linkLinkedIn,
      linkSite: profileData.linkSite,
      linkTwitter: profileData.linkTwitter,
      url: urlData.url,
    };

    const formData = serialize(update, { indices: true });
    if (profileData.pictureProfile?.file)
      formData.append('profilePicture', profileData.pictureProfile?.file);

    app.setLoading(true);
    CompanyController.update(formData)
      .then(async () => {
        setFormSubmited(false);
        alert.show({ message: 'Mudanças aplicadas com sucesso!', type: 'success' });
        setTradingName(update.tradingName ?? '');
        setAddress(update.address ?? ({} as IAddressViewModel));
        setIsServiceProvider(!!update.isServiceProvider);
        //setDistrictsSelectedsByUser(serviceData.districtsSelected);
        setSpecialtiesSelectedByUser(serviceTypeData.specialtiesSelected);
        setCorrespondencesSelectedByUser(serviceTypeData.correspondencesSelected);
        setPresentationLetter(update.presentation ?? '');
        setPictureProfile(profileData.pictureProfile);
        setExternalContacts(profileData.externalContacts);
        setLinkFacebook(update.linkFacebook ?? '');
        setLinkInstagram(update.linkInstagram ?? '');
        setLinkLinkedIn(update.linkLinkedIn ?? '');
        setLinkSite(update.linkSite ?? '');
        setLinkTwitter(update.linkTwitter ?? '');
        setUrl(update.url ?? '');
        setActiveForm('');
        const externalContactsFiltered = update?.externalContacts?.filter(
          (contact) => !!contact?.value?.trim(),
        );
        if (externalContactsFiltered && externalContactsFiltered.length > 0) {
          const contacts: NotificationPreferencesInputModel = {
            contactEmail: externalContactsFiltered.some(
              (contact) => contact.type === TypeContact.EMAIL && contact.isVerified,
            )
              ? externalContactsFiltered.find(
                  (contact) => contact.type === TypeContact.EMAIL && contact.isVerified,
                )?.value
              : undefined,
            // whatsapp: externalContactsFiltered.some(
            //   (contact) => contact.type === TypeContact.WHATSAPP && contact.isVerified,
            // )
            //   ? unMask(
            //       externalContactsFiltered.find(
            //         (contact) => contact.type === TypeContact.WHATSAPP && contact.isVerified,
            //       )!.value,
            //     )
            //   : undefined,
          };
          await UserController.createContactsForNotification(contacts);
        }
        navigate('/feed');
      })
      .catch(() => {
        alert.show({
          message: 'Falha ao tentar atualizar o perfil, tente novamente!',
          type: 'error',
        });
      })
      .finally(() => {
        app.setLoading(false);
      });
  };

  useEffect(() => {
    app.setLoading(isLoading);
  }, [app, isLoading]);

  return (
    <>
      <NavHeader typeHeader="default" />
      <Content>
        <div
          style={{
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '1rem',
          }}>
          <RegisterHeader />
          <RegisterAddress ref={addressDataRef} />
          <RegisterService ref={serviceDataRef} />
          <RegisterTypeServices ref={serviceTypeDataRef} />
          <RegisterProfile ref={profileDataRef} />
          <RegisterUrl ref={urlDataRef} />
          <InviteFriend />
          <div className="contentButton">
            <BasicButton variant="outlined" onClick={handleUpdateProfile}>
              Avançar
            </BasicButton>
          </div>
        </div>
      </Content>
    </>
  );
}
